import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import './App.css'
import './Campaigns.css'
import { Link, Redirect, useHistory } from 'react-router-dom'
import Axios from 'axios'
import TopBarProgress from 'react-topbar-progress-indicator'
import { API_URL } from './config'
import Header from './layouts/Header'
import SideNav from './layouts/SideNav'
import {
  IoIosRefresh,
  IoIosArrowBack,
  IoIosArrowDown,
  IoIosArrowUp,
  IoIosClose,
  IoIosAdd,
  IoLogoAndroid,
  IoLogoWindows,
  IoLogoChrome,
} from 'react-icons/io'
import { IoPhonePortrait } from 'react-icons/io5'
import { BsPeople, BsPen, BsGraphUp, BsArrowRight } from 'react-icons/bs'
import { BiDevices, BiMenu } from 'react-icons/bi'
import { RiMacbookLine } from 'react-icons/ri'
import { FaEdge, FaSafari, FaFirefox, FaLinux } from 'react-icons/fa'

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const ENDPOINT = `${API_URL}/apps/kscanner/webservices.php`
const PLACEHOLDER = `${API_URL}/apps/kscanner/placeholder.png`

const PAGE_SIZE = 10
const PANEL_PAGE_SIZE = 8

const api = (query, params = {}) =>
  Axios.get(ENDPOINT, { params: { query, ...params }, withCredentials: true })

// Server sends some numbers pre-formatted ("1,234.00"), so strip separators first.
const num = (value) => {
  const parsed = parseFloat(String(value ?? '0').replace(/,/g, ''))
  return Number.isNaN(parsed) ? 0 : parsed
}

const money = (value) =>
  'R' + num(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const count = (value) => num(value).toLocaleString('en-US')

const todayISO = () => new Date().toLocaleDateString('en-CA') // YYYY-MM-DD, local time

const posterUrl = (businessId, eventId, file) =>
  !file || file.trim() === '' || file === 'none'
    ? PLACEHOLDER
    : `${API_URL}/apps/kscanner/posters/${businessId}/${eventId}/${file}`

const onImageError = (e) => {
  e.currentTarget.onerror = null
  e.currentTarget.src = PLACEHOLDER
}

const statusOf = (c) =>
  c.campaignstatus === 'running' ? 'running' : c.daysRemaining === '---' ? 'ended' : 'paused'

const STATUS_LABEL = { running: 'Running', paused: 'Paused', ended: 'Ended' }
const STATUS_TITLE = {
  running: 'This campaign is live',
  paused: 'Paused or not started yet',
  ended: 'The campaign period has ended',
}

const remainingText = (c) =>
  c.daysRemaining === '---'
    ? 'Period ended'
    : `${c.daysRemaining} ${Number(c.daysRemaining) === 1 ? 'day' : 'days'} left`

const deviceIcon = (device) => {
  if (device === 'Android') return <IoLogoAndroid size={18} color="#3f9b5a" />
  if (device === 'Windows') return <IoLogoWindows size={18} color="#1664a8" />
  if (device === 'iPhone') return <IoPhonePortrait size={18} color="#5b6b7f" />
  if (device === 'MacIntel') return <RiMacbookLine size={18} color="#5b6b7f" />
  return <FaLinux size={18} color="#333" />
}

const browserIcon = (agent) => {
  if (agent === 'Edge') return <FaEdge size={18} color="#1664a8" />
  if (agent === 'Chrome') return <IoLogoChrome size={18} color="#d93025" />
  if (agent === 'Firefox') return <FaFirefox size={18} color="#e8710a" />
  if (agent === 'Safari') return <FaSafari size={18} color="#2f7fc1" />
  return <FaLinux size={18} color="#333" />
}

// State the /campaign and /analytics pages expect
const routeState = (c, fallbackBusinessId, extra = {}) => ({
  eventid: c.eventid,
  eventname: c.eventname,
  poster: posterUrl(c.businessid || fallbackBusinessId, c.eventid, c.thumbnail),
  eventdate: c.eventday,
  enddate: c.enddate,
  event_date: c.event_day,
  finish_date: c.finish_day,
  theme: c.theme,
  venue: c.venue,
  expectednum: c.expected,
  time: c.time,
  endtime: c.endtime,
  prevpage: 'campaigns',
  currentpage: 'campaigns',
  ...extra,
})

const Fact = ({ label, title, hint, children }) => (
  <div title={title}>
    <dt>{label}</dt>
    <dd>
      {children}
      {hint && <span className="cm-hint">{hint}</span>}
    </dd>
  </div>
)

const Pager = ({ page, pages, total, size, onChange }) => {
  if (total <= size) return null
  const from = (page - 1) * size + 1
  const to = Math.min(page * size, total)
  return (
    <div className="cm-pager">
      <span className="cm-pager-range">
        {from}–{to} of {total}
      </span>
      <div className="cm-pager-btns">
        <button
          type="button"
          className="cm-btn"
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
        >
          Previous
        </button>
        <span className="cm-pager-page">
          Page {page} of {pages}
        </span>
        <button
          type="button"
          className="cm-btn"
          onClick={() => onChange(page + 1)}
          disabled={page >= pages}
        >
          Next
        </button>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

const Campaignmanager = () => {
  const history = useHistory()

  const businessid = localStorage.getItem('businessid')
  const businessname = localStorage.getItem('businessname')
  const accounttype = localStorage.getItem('accounttype')

  const [details, setDetails] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const [busy, setBusy] = useState(true)
  const [expanded, setExpanded] = useState({})
  const [panel, setPanel] = useState(null) // { type, eventid, status, data }
  const [toast, setToast] = useState(null)
  const [isMob, setIsMob] = useState(window.innerWidth <= 768)
  const [collapsed, setCollapsed] = useState(window.innerWidth <= 768)
  const [page, setPage] = useState(1)
  const [panelPage, setPanelPage] = useState(1)
  const [panelQuery, setPanelQuery] = useState('')

  const panelRequest = useRef(0)
  const selectAllRef = useRef(null)

  /* ---------- derived ---------- */

  const selectedRows = useMemo(() => details.filter((c) => Number(c.selected) === 1), [details])
  const selectedCount = selectedRows.length
  const single = selectedCount === 1 ? selectedRows[0] : null
  const allSelected = details.length > 0 && selectedCount === details.length

  const totals = useMemo(() => {
    const t = { online: 0, physical: 0, admissions: 0, running: 0 }
    details.forEach((c) => {
      t.online += num(c.digitalRevenue)
      t.physical += num(c.physicalRevenue)
      t.admissions += num(c.admission)
      if (c.campaignstatus === 'running') t.running += 1
    })
    return t
  }, [details])

  const pages = Math.max(1, Math.ceil(details.length / PAGE_SIZE))
  const currentPage = Math.min(page, pages) // clamps if rows disappear
  const pagedDetails = details.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  /* ---------- data ---------- */

  const notify = useCallback((message, tone = 'info') => {
    setToast({ message, tone, id: Date.now() })
  }, [])

  const loadCampaigns = useCallback(() => {
    setBusy(true)
    return api('CAMPAIGNS', { businessid })
      .then((res) => {
        setDetails(Array.isArray(res.data) ? res.data : [])
        setLoadFailed(false)
      })
      .catch((err) => {
        console.log(err)
        setLoadFailed(true)
      })
      .finally(() => {
        setLoaded(true)
        setBusy(false)
      })
  }, [businessid])

  useEffect(() => {
    document.title = 'Campaigns | ' + businessname
    if (businessid === null) return
    loadCampaigns()
  }, [businessid, businessname, loadCampaigns])

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= 768
      setIsMob(mobile)
      setCollapsed((prev) => (mobile ? true : prev))
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    if (!toast) return undefined
    const timer = setTimeout(() => setToast(null), 5000)
    return () => clearTimeout(timer)
  }, [toast])

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = selectedCount > 0 && !allSelected
    }
  }, [selectedCount, allSelected])

  // Close the device/buyer panel if the selection moves away from its campaign
  useEffect(() => {
    if (panel && panel.type !== 'create' && (!single || single.eventid !== panel.eventid)) {
      setPanel(null)
    }
  }, [panel, single])

  useEffect(() => {
    if (!panel) return undefined
    const onKey = (e) => e.key === 'Escape' && setPanel(null)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [panel])

  /* ---------- actions ---------- */

  const runAction = (query, params, onDone) => {
    if (busy) return
    setBusy(true)
    api(query, params)
      .then((res) => onDone && onDone(res.data))
      .catch((err) => {
        console.log(err)
        notify("Couldn't complete that action. Check your connection and try again.", 'error')
      })
      .finally(loadCampaigns)
  }

  const toggleRunning = (c) =>
    runAction('RESUMECAMPAIGN', { eventid: c.eventid, businessid }, (data) => {
      if (!data) return
      if (data.status === 'failed') notify(data.message, 'error')
      else if (data.message) notify(data.message, 'success')
    })

  const toggleSelected = (c) =>
    runAction('UPDATESELECT', { eventid: c.eventid, accountid: businessid })

  const toggleSelectAll = () => runAction('UPDATESELECT', { businessid })

  const resumeSelected = () => runAction('RESUMECAMPAIGNS', { businessid })

  const toggleExpanded = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))

  const goEdit = (c) => history.push({ pathname: '/campaign', state: routeState(c, businessid) })

  const goAnalytics = (c) =>
    history.push({
      pathname: '/analytics',
      state: routeState(c, businessid, { back: 'campaigns' }),
    })

  const closePanel = () => {
    panelRequest.current += 1
    setPanel(null)
  }

  const openPanel = (type) => {
    const request = ++panelRequest.current
    const eventid = single ? single.eventid : null
    setPanelPage(1)
    setPanelQuery('')
    setPanel({ type, eventid, status: 'loading', data: [] })

    let call
    if (type === 'devices') call = api('PLATFORMS', { businessid, eventid })
    else if (type === 'buyers')
      call = api('CLIENTELE', { subquery: 'GETCLIENTELE', eventid, businessid })
    else call = api('MYEVENTS', { accountid: businessid, accounttype, thumbnail: 'yes' })

    call
      .then((res) => {
        if (request !== panelRequest.current) return
        const data = Array.isArray(res.data) ? res.data : []
        setPanel({ type, eventid, status: 'ready', data })
      })
      .catch((err) => {
        console.log(err)
        if (request !== panelRequest.current) return
        setPanel({ type, eventid, status: 'error', data: [] })
      })
  }

  const onPanelSearch = (e) => {
    setPanelQuery(e.target.value)
    setPanelPage(1)
  }

  const handleToggleSidebar = () => setCollapsed((prev) => !prev)

  /* ---------- guards ---------- */

  if (businessid === null) {
    return <Redirect to="/login" />
  }

  /* ---------- render pieces ---------- */

  const selectionLabel =
    selectedCount === 1 ? single.campaignname : `${selectedCount} campaigns selected`

  const subtitle = !loaded
    ? 'Loading your campaigns'
    : details.length === 0
      ? 'Promote an event to start selling tickets'
      : `${details.length} ${details.length === 1 ? 'campaign' : 'campaigns'}, ${totals.running} running`

  const renderRow = (c) => {
    const status = statusOf(c)
    const isSel = Number(c.selected) === 1
    const isOpen = !!expanded[c.campaignid]
    const online = num(c.digitalRevenue)
    const physical = num(c.physicalRevenue)
    const fee = num(c.feesDeductedPhysical) + num(c.feesDeductedDigital)

    return (
      <React.Fragment key={c.campaignid}>
        <tr className={isSel ? 'is-selected' : ''}>
          <td className="cm-c-check">
            <input
              type="checkbox"
              className="cm-check"
              checked={isSel}
              onChange={() => toggleSelected(c)}
              aria-label={`Select ${c.campaignname}`}
            />
          </td>

          <td>
            <div className="cm-name">
              <img
                className="cm-poster"
                src={posterUrl(c.businessid || businessid, c.eventid, c.thumbnail)}
                onError={onImageError}
                alt=""
              />
              <div className="cm-name-text">
                <Link
                  className="cm-name-link"
                  to={{ pathname: '/campaign', state: routeState(c, businessid) }}
                  title={c.campaignname}
                >
                  {c.campaignname}
                </Link>
                <span className="cm-muted" title={c.eventname}>
                  {c.eventname}
                </span>
              </div>
            </div>
          </td>

          <td>
            <div className="cm-status">
              <button
                type="button"
                role="switch"
                aria-checked={status === 'running'}
                aria-label={`${status === 'running' ? 'Pause' : 'Resume'} ${c.campaignname}`}
                className={`cm-switch ${status === 'running' ? 'on' : ''}`}
                onClick={() => toggleRunning(c)}
                disabled={busy}
              >
                <span />
              </button>
              <span className={`cm-badge ${status}`} title={STATUS_TITLE[status]}>
                {STATUS_LABEL[status]}
              </span>
            </div>
          </td>

          <td>
            <span className="cm-strong">
              {c.fromdate} – {c.todate}
            </span>
            <span className="cm-muted">{remainingText(c)}</span>
          </td>

          <td className="is-num num">
            <span className="cm-strong">{money(online + physical)}</span>
            <span className="cm-muted">
              {money(online)} online, {money(physical)} physical
            </span>
          </td>

          <td className="is-num num">
            <span className="cm-strong">{money(fee)}</span>
          </td>

          <td className="is-num num">
            <span className="cm-strong">{count(c.admission)}</span>
            <span className="cm-muted">{count(c.buyers)} unique buyers</span>
          </td>

          <td className="is-num num">
            <span className="cm-strong">{count(c.views)} views</span>
            <span className="cm-muted">{count(c.eventClicks)} clicks</span>
          </td>

          <td>
            <button
              type="button"
              className="cm-expand"
              onClick={() => toggleExpanded(c.campaignid)}
              aria-expanded={isOpen}
              aria-label={`${isOpen ? 'Hide' : 'Show'} details for ${c.campaignname}`}
            >
              {isOpen ? <IoIosArrowUp size={18} /> : <IoIosArrowDown size={18} />}
            </button>
          </td>
        </tr>

        {isOpen && (
          <tr className="cm-detail">
            <td colSpan={9}>
              <dl className="cm-facts">
                <Fact label="Keywords" title="Keywords used to promote this campaign">
                  {c.keywords || 'None'}
                </Fact>
                <Fact label="Event date" title="The date the event takes place">
                  {c.eventday}
                </Fact>
                <Fact label="Campaign starts">{c.fromdate}</Fact>
                <Fact label="Campaign ends">{c.todate}</Fact>
                <Fact label="Running period">{c.getPeriod} days</Fact>
                <Fact
                  label="Estimated revenue"
                  title="Projected income if every loaded ticket sells"
                  hint="If every loaded ticket sells"
                >
                  {money(c.estdRevenue)}
                </Fact>
                <Fact
                  label="Platform fee"
                  title="3.5% commission from ticket sales, deducted before payout"
                  hint="Deducted before payout"
                >
                  {money(fee)}
                </Fact>
                <Fact label="Unique clicks" title="Distinct users who clicked, counted once">
                  {count(c.unique_clicks)}
                </Fact>
                <Fact
                  label="Unique visitors"
                  title="Distinct users who viewed the event, counted once per session or login"
                >
                  {count(c.unique_visitors)}
                </Fact>
              </dl>
            </td>
          </tr>
        )}
      </React.Fragment>
    )
  }

  const renderTableBody = () => {
    if (!loaded) {
      return (
        <tr>
          <td colSpan={9}>
            <div className="cm-empty">
              <p>Loading campaigns</p>
            </div>
          </td>
        </tr>
      )
    }

    if (loadFailed) {
      return (
        <tr>
          <td colSpan={9}>
            <div className="cm-empty">
              <h3>We couldn't load your campaigns</h3>
              <p>Check your connection, then try again.</p>
              <button type="button" className="cm-btn cm-btn-primary" onClick={loadCampaigns}>
                Try again
              </button>
            </div>
          </td>
        </tr>
      )
    }

    if (details.length === 0) {
      return (
        <tr>
          <td colSpan={9}>
            <div className="cm-empty">
              <h3>No campaigns yet</h3>
              <p>Create a campaign to start promoting an event and selling tickets.</p>
              <button
                type="button"
                className="cm-btn cm-btn-primary"
                onClick={() => openPanel('create')}
              >
                <IoIosAdd size={20} /> Create campaign
              </button>
            </div>
          </td>
        </tr>
      )
    }

    return pagedDetails.map(renderRow)
  }

  const renderPanelBody = () => {
    if (panel.status === 'loading') return <p className="cm-drawer-note">Loading</p>
    if (panel.status === 'error')
      return <p className="cm-drawer-note">We couldn't load this. Close the panel and try again.</p>

    if (panel.type === 'devices') {
      if (panel.data.length < 1)
        return (
          <p className="cm-drawer-note">
            No visits recorded yet. Devices appear once the event page has been opened.
          </p>
        )
      return (
        <table className="cm-devices">
          <thead>
            <tr>
              <th>
                <BiDevices size={16} /> Device
              </th>
              <th>Browser</th>
              <th className="is-num" style={{ textAlign: 'right' }}>
                Visits
              </th>
            </tr>
          </thead>
          <tbody>
            {panel.data.map((item, i) => (
              <tr key={`${item.device}-${item.useragent}-${i}`}>
                <td>
                  <span className="with-icon">
                    {deviceIcon(item.device)}
                    {item.device === 'MacIntel' ? 'Mac' : item.device}
                  </span>
                </td>
                <td>
                  <span className="with-icon">
                    {browserIcon(item.useragent)}
                    {item.useragent}
                  </span>
                </td>
                <td className="num" style={{ textAlign: 'right' }}>
                  {count(item.platformCount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )
    }

    if (panel.type === 'buyers') {
      if (panel.data.length < 1)
        return <p className="cm-drawer-note">No one has bought tickets for this campaign yet.</p>
      return (
        <ul className="cm-list">
          {panel.data.map((item) => (
            <li key={item.clientid}>
              <Link
                className="cm-row"
                to={{
                  pathname: '/customerprofile',
                  state: {
                    accountid: businessid,
                    accounttype,
                    clientid: item.clientid,
                    phone: item.phone,
                    clientename: item.fname + ' ' + item.lname,
                    email: item.email,
                    prevpage: 'customers',
                  },
                }}
              >
                <div className="cm-row-main">
                  <strong>
                    {item.fname} {item.lname}
                  </strong>
                  <span className="cm-muted">{item.email || item.phone}</span>
                </div>
                <BsArrowRight />
              </Link>
            </li>
          ))}
        </ul>
      )
    }

    // create
    if (panel.data.length < 1)
      return (
        <p className="cm-drawer-note">
          No events are ready for a campaign. Create an event and upload its poster first.
        </p>
      )

    const today = todayISO()
    const q = panelQuery.trim().toLowerCase()
    const filtered = q
      ? panel.data.filter((item) =>
          `${item.eventname || ''} ${item.event_day || ''} ${item.venue || ''}`
            .toLowerCase()
            .includes(q),
        )
      : panel.data
    const panelPages = Math.max(1, Math.ceil(filtered.length / PANEL_PAGE_SIZE))
    const panelCurrent = Math.min(panelPage, panelPages)
    const visibleEvents = filtered.slice(
      (panelCurrent - 1) * PANEL_PAGE_SIZE,
      panelCurrent * PANEL_PAGE_SIZE,
    )

    return (
      <>
        <div className="cm-search">
          <input
            type="search"
            className="cm-search-input"
            placeholder="Search events by name, date or venue"
            value={panelQuery}
            onChange={onPanelSearch}
            aria-label="Search events"
          />
        </div>

        {filtered.length < 1 ? (
          <p className="cm-drawer-note">No events match "{panelQuery.trim()}".</p>
        ) : (
          <ul className="cm-list">
            {visibleEvents.map((item) => {
              const past = item.event_day < today
              const inner = (
                <>
                  <img
                    className="cm-poster"
                    src={posterUrl(item.businessid || businessid, item.eventid, item.thumbnail)}
                    onError={onImageError}
                    alt=""
                  />
                  <div className="cm-row-main">
                    <strong>{item.eventname}</strong>
                    <span className="cm-muted">
                      {past ? `${item.event_day}, the event date has passed` : item.event_day}
                    </span>
                  </div>
                  {!past && <BsArrowRight />}
                </>
              )
              return (
                <li key={item.eventid}>
                  {past ? (
                    <div className="cm-row is-past">{inner}</div>
                  ) : (
                    <Link
                      className="cm-row"
                      to={{
                        pathname: '/campaign',
                        state: {
                          eventid: item.eventid,
                          eventname: item.eventname,
                          poster: posterUrl(
                            item.businessid || businessid,
                            item.eventid,
                            item.thumbnail,
                          ),
                          eventdate: item.eventday,
                          enddate: item.enddate,
                          event_date: item.event_day,
                          finish_date: item.finish_day,
                          theme: item.theme,
                          venue: item.venue,
                          expectednum: item.expected,
                          time: item.time,
                          endtime: item.endtime,
                          prevpage: 'campaigns',
                          currentpage: 'campaigns',
                        },
                      }}
                    >
                      {inner}
                    </Link>
                  )}
                </li>
              )
            })}
          </ul>
        )}

        <Pager
          page={panelCurrent}
          pages={panelPages}
          total={filtered.length}
          size={PANEL_PAGE_SIZE}
          onChange={setPanelPage}
        />
      </>
    )
  }

  const panelTitle = panel
    ? {
        devices: 'Devices and browsers',
        buyers: 'Unique buyers',
        create: 'Choose an event',
      }[panel.type]
    : ''

  const panelSub = panel
    ? panel.type === 'create'
      ? 'Pick the event you want to promote.'
      : single
        ? single.campaignname
        : ''
    : ''

  /* ---------- layout ---------- */

  return (
    <div className="view">
      {busy && <TopBarProgress />}
      <Header />
      <SideNav collapsed={collapsed} onToggle={handleToggleSidebar} />

      <div
        className="main-content"
        style={{
          marginTop: 40,
          marginLeft: isMob ? '0px' : collapsed ? '60px' : '240px',
        }}
      >
        {isMob && !collapsed && (
          <div className="mobile-overlay active" onClick={handleToggleSidebar} />
        )}

        {isMob && collapsed && (
          <label className="mobile-toggle" onClick={handleToggleSidebar}>
            <BiMenu size={25} />
          </label>
        )}

        <div className="cm">
          {/* Page header */}
          <div className="cm-top mt-4">
            <div>
              <button type="button" className="cm-back" onClick={() => history.goBack()}>
                <IoIosArrowBack size={18} /> Back
              </button>
              <h1 className="cm-title">Campaigns</h1>
              <p className="cm-sub">{subtitle}</p>
            </div>
            <div className="cm-top-actions">
              <button
                type="button"
                className={`cm-btn cm-btn-icon ${busy ? 'cm-spin' : ''}`}
                onClick={loadCampaigns}
                disabled={busy}
                aria-label="Refresh campaigns"
                title="Refresh"
              >
                <IoIosRefresh size={20} />
              </button>
              <button
                type="button"
                className="cm-btn cm-btn-primary"
                onClick={() => openPanel('create')}
              >
                <IoIosAdd size={20} /> Create campaign
              </button>
            </div>
          </div>

          {/* Summary band */}
          {details.length > 0 && (
            <div className="cm-band num">
              <div className="cm-band-item is-lead">
                <span className="cm-band-label">Total ticket revenue</span>
                <span className="cm-band-value">{money(totals.online + totals.physical)}</span>
                <span className="cm-band-note">Across all {details.length} campaigns</span>
              </div>
              <div className="cm-band-item">
                <span className="cm-band-label">Online</span>
                <span className="cm-band-value">{money(totals.online)}</span>
              </div>
              <div className="cm-band-item">
                <span className="cm-band-label">Physical</span>
                <span className="cm-band-value">{money(totals.physical)}</span>
              </div>
              <div className="cm-band-item">
                <span className="cm-band-label">Admissions</span>
                <span className="cm-band-value">{count(totals.admissions)}</span>
              </div>
              <div className="cm-band-item">
                <span className="cm-band-label">Running now</span>
                <span className="cm-band-value">{totals.running}</span>
              </div>
            </div>
          )}

          {/* Selection bar */}
          {selectedCount > 0 && (
            <div className="cm-selbar" role="region" aria-label="Selected campaigns">
              <div className="cm-selbar-text">
                {selectionLabel}
                {selectedCount > 1 && (
                  <span className="cm-selbar-hint">
                    Select one campaign to edit it or see its analytics
                  </span>
                )}
              </div>
              <div className="cm-selbar-actions">
                {single ? (
                  <>
                    <button type="button" className="cm-btn" onClick={() => goEdit(single)}>
                      <BsPen /> Edit
                    </button>
                    <button type="button" className="cm-btn" onClick={() => goAnalytics(single)}>
                      <BsGraphUp /> Analytics
                    </button>
                    <button type="button" className="cm-btn" onClick={() => openPanel('devices')}>
                      <BiDevices /> Devices
                    </button>
                    <button type="button" className="cm-btn" onClick={() => openPanel('buyers')}>
                      <BsPeople /> Unique buyers
                    </button>
                  </>
                ) : (
                  <button type="button" className="cm-btn" onClick={resumeSelected} disabled={busy}>
                    Pause or resume all selected
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Campaigns table */}
          <div className="cm-card">
            <div className="cm-scroll">
              <table className="cm-table">
                <thead>
                  <tr>
                    <th className="cm-c-check">
                      <input
                        ref={selectAllRef}
                        type="checkbox"
                        className="cm-check"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        disabled={details.length === 0}
                        aria-label="Select all campaigns"
                      />
                    </th>
                    <th>Campaign</th>
                    <th>Status</th>
                    <th>Running period</th>
                    <th className="is-num">Revenue</th>
                    <th
                      className="is-num"
                      title="3.5% commission from ticket sales, deducted before payout"
                    >
                      Platform fee
                    </th>
                    <th className="is-num" title="People expected to attend, based on tickets sold">
                      Admissions
                    </th>
                    <th className="is-num">Reach</th>
                    <th>
                      <span style={{ position: 'absolute', left: -9999 }}>Details</span>
                    </th>
                  </tr>
                </thead>
                <tbody>{renderTableBody()}</tbody>
              </table>
            </div>
            <Pager
              page={currentPage}
              pages={pages}
              total={details.length}
              size={PAGE_SIZE}
              onChange={setPage}
            />
          </div>
        </div>
      </div>

      {/* Side panel */}
      {panel && (
        <div className="cm-scrim cm-root" onClick={closePanel}>
          <aside
            className="cm-drawer"
            role="dialog"
            aria-modal="true"
            aria-label={panelTitle}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cm-drawer-head">
              <div>
                <h2>{panelTitle}</h2>
                {panelSub && <p>{panelSub}</p>}
              </div>
              <button
                type="button"
                className="cm-btn cm-btn-icon"
                onClick={closePanel}
                aria-label="Close panel"
                autoFocus
              >
                <IoIosClose size={26} />
              </button>
            </div>
            <div className="cm-drawer-body">{renderPanelBody()}</div>
            {panel.type === 'create' && (
              <div className="cm-drawer-foot">
                <Link
                  className="cm-btn cm-btn-primary cm-btn-block"
                  to={{ pathname: '/addevent', state: { prevpage: 'dashboard' } }}
                >
                  <IoIosAdd size={20} /> Create a new event
                </Link>
              </div>
            )}
          </aside>
        </div>
      )}

      {toast && (
        <div className="cm-root">
          <div
            key={toast.id}
            className={`cm-toast ${toast.tone}`}
            role="status"
            onClick={() => setToast(null)}
          >
            {toast.message}
          </div>
        </div>
      )}
    </div>
  )
}

export default Campaignmanager
